import React, { useEffect, useState } from "react";
import "./Navbar.css";
import { getCustomElementUrl } from "../../utils/cloudinary";

// Cloudinary URLs for images
const whiteArrow = getCustomElementUrl('white_arrow_to_be_used_on_buttons');
const hamburger_menu = getCustomElementUrl('hamburger_menu');
const hamburger_cross = getCustomElementUrl('hamburger_cross');

// Add a global variable to track animation state
let isAnimationActive = true;

// Function to set animation state
export const setAnimationState = (active: boolean) => {
    isAnimationActive = active;
};

export const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [animationActive, setAnimationActive] = useState(isAnimationActive);

    // Required distance between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        // Only allow left swipes within 50px of right edge when menu is closed
        const isNearRightEdge = window.innerWidth - touchStart < 50;

        if (isLeftSwipe && isNearRightEdge && !isMenuOpen) {
            setIsMenuOpen(true);
        } else if (isRightSwipe && isMenuOpen) {
            setIsMenuOpen(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        const handleResize = () => {
            setIsMobile(window.innerWidth < 700);
        };

        const handleClickOutside = (event: MouseEvent) => {
            const sidebarMenu = document.querySelector(".sidebar-menu");
            const hamburgerButton = document.querySelector(".hamburger-menu");

            if (
                sidebarMenu &&
                hamburgerButton &&
                !sidebarMenu.contains(event.target as Node) &&
                !hamburgerButton.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        // Check animation state periodically
        const animationCheckInterval = setInterval(() => {
            // Only update animation state if not scrolled
            if (!isScrolled) {
                setAnimationActive(isAnimationActive);
            }
        }, 100);

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("mousedown", handleClickOutside);
            clearInterval(animationCheckInterval);
        };
    }, [isScrolled]);

    useEffect(() => {
        // Disable/enable scrolling when menu opens/closes
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [isMenuOpen]);

    const scrollToTop = () => {
        console.log("scrollToTop");
        document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <>
            {isMobile && !isMenuOpen && (
                <div
                    className="edge-swipe-detector"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                />
            )}
            <nav
                className={`navbar ${isScrolled ? "scrolled" : ""} ${
                    isMenuOpen ? "menu-open" : ""
                } ${!isScrolled && animationActive ? "animation-active" : ""}`}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {!isMobile ? (
                    <>
                        <div className="navbar-logo" onClick={scrollToTop}>
                            JMNB
                        </div>
                        <ul className="navbar-links">
                            <li onClick={() => scrollToSection("about")}>
                                About
                            </li>
                            <li onClick={() => scrollToSection("projects")}>
                                Projects
                            </li>
                            <li onClick={() => scrollToSection("skills")}>
                                Skills
                            </li>
                            <li onClick={() => scrollToSection("interests")}>
                                Interests
                            </li>
                        </ul>
                        <button
                            className="navbar-button"
                            onClick={() => scrollToSection("contact")}
                        >
                            Contact Me{" "}
                            <img
                                src={whiteArrow}
                                alt="arrow"
                                className="arrow-icon-navbar-button"
                            />{" "}
                        </button>
                    </>
                ) : (
                    <>
                        <div className="navbar-logo" onClick={scrollToTop}>
                            JMNB
                        </div>
                        <img
                            src={hamburger_menu}
                            className="hamburger-menu"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            alt="hamburger menu"
                        />
                    </>
                )}
            </nav>
            <>
                <div
                    className={`sidebar-menu ${isMenuOpen ? "open" : ""}`}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <div
                        className="sidebar-menu-close-button"
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <img src={hamburger_cross} alt="close menu" />
                    </div>
                    <div className="sidebar-menu-content-container">
                        <div
                            className="sidebar-menu-logo"
                            onClick={() => {
                                scrollToTop();
                                setIsMenuOpen(false);
                            }}
                        >
                            JMNB
                        </div>
                        <div className="sidebar-menu-divider" />
                        <div className="sidebar-menu-links-container">
                            <div
                                className="sidebar-menu-link"
                                onClick={() => {
                                    scrollToSection("about");
                                    setIsMenuOpen(false);
                                }}
                            >
                                About
                            </div>
                            <div
                                className="sidebar-menu-link"
                                onClick={() => {
                                    scrollToSection("projects");
                                    setIsMenuOpen(false);
                                }}
                            >
                                Projects
                            </div>
                            <div
                                className="sidebar-menu-link"
                                onClick={() => {
                                    scrollToSection("skills");
                                    setIsMenuOpen(false);
                                }}
                            >
                                Skills
                            </div>
                            <div
                                className="sidebar-menu-link"
                                onClick={() => {
                                    scrollToSection("interests");
                                    setIsMenuOpen(false);
                                }}
                            >
                                Interests
                            </div>
                        </div>
                        <div className="sidebar-menu-divider" />
                        <div
                            className="sidebar-menu-link"
                            onClick={() => {
                                scrollToSection("contact");
                                setIsMenuOpen(false);
                            }}
                        >
                            Contact Me
                        </div>
                    </div>
                </div>
            </>
        </>
    );
};

export default Navbar;
